/**
 * SessionCreator.js - Create sync sessions by assigning videos to displays
 */

import React, { useState } from 'react';
import axiosInstance from '../../axiosInstance';

const SessionCreator = ({ sessions, videos, displays, onRefresh }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [formData, setFormData] = useState({
    sessionName: '',
    description: '',
    assignments: [],
    driftThreshold: 100,
    autoCorrect: true,
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAssignmentChange = (displayId, videoId) => {
    setFormData(prev => {
      const existingIndex = prev.assignments.findIndex(a => a.display === displayId);

      if (existingIndex >= 0) {
        // Update existing assignment
        const newAssignments = [...prev.assignments];
        newAssignments[existingIndex] = { display: displayId, video: videoId };
        return { ...prev, assignments: newAssignments };
      } else {
        // Add new assignment
        return {
          ...prev,
          assignments: [...prev.assignments, { display: displayId, video: videoId }],
        };
      }
    });
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();

    if (formData.assignments.length === 0) {
      alert('Please assign at least one video to a display');
      return;
    }

    try {
      await axiosInstance.post('/video-sync/sessions', formData);
      alert('Session created successfully!');
      setShowCreateModal(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('[SessionCreator] Create session error:', error);
      alert('Failed to create session: ' + (error.response?.data?.error || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      sessionName: '',
      description: '',
      assignments: [],
      driftThreshold: 100,
      autoCorrect: true,
    });
  };

  const prepareSession = async (sessionId) => {
    if (!window.confirm('This will deploy videos to all assigned displays via ADB. Continue?')) return;

    try {
      const response = await axiosInstance.post(`/video-sync/sessions/${sessionId}/prepare`);
      const { deploymentResults } = response.data;

      const successCount = deploymentResults.filter(r => r.success).length;
      const failCount = deploymentResults.filter(r => !r.success).length;

      alert(
        `Deployment Complete!\n\n` +
        `✅ Success: ${successCount}\n` +
        `❌ Failed: ${failCount}\n\n` +
        deploymentResults.map(r =>
          `${r.display} → ${r.video}: ${r.success ? '✅' : '❌' + (r.error || '')}`
        ).join('\n')
      );

      onRefresh();
    } catch (error) {
      console.error('[SessionCreator] Prepare session error:', error);
      alert('Preparation failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const playSession = async (sessionId) => {
    try {
      await axiosInstance.post(`/video-sync/sessions/${sessionId}/play`);
      alert('Playback started on all displays!');
      onRefresh();
    } catch (error) {
      console.error('[SessionCreator] Play session error:', error);
      alert('Failed to start playback: ' + (error.response?.data?.error || error.message));
    }
  };

  const stopSession = async (sessionId) => {
    try {
      await axiosInstance.post(`/video-sync/sessions/${sessionId}/stop`);
      alert('Session stopped');
      onRefresh();
    } catch (error) {
      console.error('[SessionCreator] Stop session error:', error);
      alert('Failed to stop session');
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) return;

    try {
      await axiosInstance.delete(`/video-sync/sessions/${sessionId}`);
      alert('Session deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('[SessionCreator] Delete error:', error);
      alert('Delete failed');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      preparing: { color: '#3498db', text: 'Preparing' },
      ready: { color: '#27ae60', text: 'Ready' },
      playing: { color: '#2ecc71', text: 'Playing' },
      paused: { color: '#f39c12', text: 'Paused' },
      stopped: { color: '#95a5a6', text: 'Stopped' },
      error: { color: '#e74c3c', text: 'Error' },
    };

    const badge = badges[status] || badges.preparing;

    return (
      <span style={{
        backgroundColor: badge.color,
        color: 'white',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
      }}>
        {badge.text}
      </span>
    );
  };

  return (
    <div className="session-creator">
      <div className="creator-header">
        <h2>Sync Sessions</h2>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          + Create Session
        </button>
      </div>

      {/* Sessions List */}
      <div className="sessions-list">
        {sessions.length === 0 ? (
          <div className="empty-state">
            <p>No sessions created yet. Create your first session above!</p>
          </div>
        ) : (
          sessions.map((session) => (
            <div key={session._id} className="session-card">
              <div className="session-header">
                <div>
                  <h3>{session.sessionName}</h3>
                  <p className="session-description">{session.description}</p>
                </div>
                {getStatusBadge(session.status)}
              </div>

              <div className="session-assignments">
                <h4>Assignments ({session.assignments.length}):</h4>
                <div className="assignment-grid">
                  {session.assignments.map((assignment, index) => (
                    <div key={index} className="assignment-item">
                      <div className="assignment-display">
                        📺 {assignment.display?.name || 'Unknown Display'}
                      </div>
                      <div className="assignment-arrow">→</div>
                      <div className="assignment-video">
                        🎬 {assignment.video?.name || 'Unknown Video'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="session-settings">
                <span className="setting-item">
                  <strong>Drift Threshold:</strong> {session.driftThreshold}ms
                </span>
                <span className="setting-item">
                  <strong>Auto-Correct:</strong> {session.autoCorrect ? 'Yes' : 'No'}
                </span>
                {session.stats && (
                  <span className="setting-item">
                    <strong>Avg Drift:</strong> {Math.round(session.stats.averageDrift)}ms
                  </span>
                )}
              </div>

              <div className="session-actions">
                {session.status === 'preparing' && (
                  <button
                    className="btn-primary"
                    onClick={() => prepareSession(session._id)}
                  >
                    📤 Deploy Videos
                  </button>
                )}
                {(session.status === 'ready' || session.status === 'stopped' || session.status === 'paused') && (
                  <button
                    className="btn-success"
                    onClick={() => playSession(session._id)}
                  >
                    ▶️ {session.status === 'stopped' ? 'Restart' : 'Play'}
                  </button>
                )}
                {session.status === 'playing' && (
                  <button
                    className="btn-warning"
                    onClick={() => stopSession(session._id)}
                  >
                    ⏹️ Stop
                  </button>
                )}
                <button
                  className="btn-danger"
                  onClick={() => deleteSession(session._id)}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>Create Sync Session</h2>
            <button className="close-btn" onClick={() => setShowCreateModal(false)}>✕</button>

            <form onSubmit={handleCreateSession}>
              <div className="form-group">
                <label>Session Name *</label>
                <input
                  type="text"
                  name="sessionName"
                  value={formData.sessionName}
                  onChange={handleInputChange}
                  placeholder="e.g., Lobby Video Wall"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of this session..."
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>Assign Videos to Displays *</label>
                <div className="assignment-builder">
                  {displays.map((display) => (
                    <div key={display._id} className="assignment-row">
                      <div className="display-label">
                        📺 {display.name}
                      </div>
                      <select
                        onChange={(e) => handleAssignmentChange(display._id, e.target.value)}
                        value={formData.assignments.find(a => a.display === display._id)?.video || ''}
                      >
                        <option value="">-- Select Video --</option>
                        {videos.filter(v => v.status === 'ready').map((video) => (
                          <option key={video._id} value={video._id}>
                            {video.name} ({video.duration}s)
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Drift Threshold (ms)</label>
                  <input
                    type="number"
                    name="driftThreshold"
                    value={formData.driftThreshold}
                    onChange={handleInputChange}
                    min="10"
                    max="1000"
                  />
                  <small>Alert when drift exceeds this value</small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="autoCorrect"
                      checked={formData.autoCorrect}
                      onChange={handleInputChange}
                    />
                    Enable Auto-Correction
                  </label>
                  <small>Automatically adjust playback to maintain sync</small>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Session
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionCreator;
